import { CLINIC_OPTIONS } from "./clinic.utils";
import { PATIENT_CURRENT_APPOINTMENT_OPTIONS } from "./patient-appointments.utils";
import { PATIENT_NAME_OPTIONS } from "./patient-info/patient-name.utils";
import { PRIMARY_CARE_PHYSICIAN_OPTIONS } from "./patient-info/primary-care-physician.utils";
import { REFERRING_PHYSICIAN_OPTIONS } from "./patient-info/referring-physician.utils";
import { PATIENT_MEDICARE_INSURANCE_POLICY_OPTIONS } from "./patient-medicare-insurance-policy.utils";
import { PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS } from "./patient-primary-insurance-policy.utils";
import { PATIENT_SECONDARY_INSURANCE_POLICY_OPTIONS } from "./patient-secondary-insurance-policy.utils";
import { type MergeTagMenuEntry } from "./signature-merge-tags.utils";

// Merge tags added by SN-19877 so Digital Forms can fill what Anvil fills, gated behind the
// `anvil-merge-tag-parity` flag. With the flag off these entries are stripped from the picker and the
// form converter's catalog, so both match the pre-SN-19877 tree. Deleting this file (and its call
// sites) is the whole flag cleanup.
export const ANVIL_PARITY_MERGE_TAG_VALUES = new Set<string>([
  // §1 patient details
  PATIENT_NAME_OPTIONS.MiddleInitial,
  REFERRING_PHYSICIAN_OPTIONS.FIRST_NAME,
  REFERRING_PHYSICIAN_OPTIONS.LAST_NAME,
  PRIMARY_CARE_PHYSICIAN_OPTIONS.FIRST_NAME,
  PRIMARY_CARE_PHYSICIAN_OPTIONS.LAST_NAME,
  // §2 policy holders
  PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_FULL_NAME,
  PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_MIDDLE_INITIAL,
  PATIENT_SECONDARY_INSURANCE_POLICY_OPTIONS.SECONDARY_INSURANCE_POLICY_HOLDER_FULL_NAME,
  PATIENT_SECONDARY_INSURANCE_POLICY_OPTIONS.SECONDARY_INSURANCE_POLICY_HOLDER_MIDDLE_INITIAL,
  // §3 Medicare: the whole group is new
  ...Object.values(PATIENT_MEDICARE_INSURANCE_POLICY_OPTIONS),
  // §4 current appointment
  PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_TIME,
  PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_PROVIDER_LICENSE_NUMBER,
  PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_REFERRAL_SOURCE,
  PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_DIRECT_SUPERVISING_PROVIDER_FULL_NAME,
  PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_DIRECT_SUPERVISING_PROVIDER_SIGNATURE,
  PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_SUPERVISING_PROVIDER_FULL_NAME,
  PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_SUPERVISING_PROVIDER_SIGNATURE,
  // §7 clinic, billing and practice
  CLINIC_OPTIONS.CLINIC_STREET_ADDRESS,
  CLINIC_OPTIONS.CLINIC_CITY_STATE_ZIP,
  CLINIC_OPTIONS.CLINIC_BILLING_STREET_ADDRESS,
  CLINIC_OPTIONS.CLINIC_BILLING_CITY_STATE_ZIP,
  CLINIC_OPTIONS.CLINIC_BILLING_FULL_ADDRESS,
  CLINIC_OPTIONS.CLINIC_BILLING_PHONE_NUMBER,
  CLINIC_OPTIONS.PRACTICE_NPI
]);

// Drops parity entries, then drops any submenu that filtering left empty. Entries it doesn't touch
// keep their shape, so the flag-off tree is the pre-SN-19877 tree exactly.
export const stripAnvilParityMergeTags = <T extends MergeTagMenuEntry>(
  group: T
): T => {
  if (!group.menu) return group;

  const menu = group.menu.flatMap((entry) => {
    if (entry.value && ANVIL_PARITY_MERGE_TAG_VALUES.has(entry.value))
      return [];
    if (!entry.menu?.length) return [entry];

    const stripped = stripAnvilParityMergeTags(entry);
    return stripped.menu?.length ? [stripped] : [];
  });

  return { ...group, menu };
};
